import React from 'react';
import styles from './SchedulingFlow.module.css';
import { getProviderSpecificOpenSlots, rescheduleAppointment } from '../../utils/apis';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
// Provider image imports
import heatherMarkhamImage from './SVGs/providerimages/Heather_Markham 2.png';
import kaylaSharpeImage from './SVGs/providerimages/Kayla_Sharpe.png';
import benEgbersImage from './SVGs/providerimages/Ben_Egbers_Boulder_Psychiatrist.png';
import katharineChurchImage from './SVGs/providerimages/Katharine_Selina_Church.png';
import tiffanyWalkerImage from './SVGs/providerimages/Tiffany_Walker.png';
import elinorObuckleyImage from './SVGs/providerimages/Elinor-Obuckley.png';
import sarahMclaughlinImage from './SVGs/providerimages/sarah-mclaughlin.png';
import francesRhodesImage from './SVGs/providerimages/frances-borger-rhodes.png';
import andrewWennerImage from './SVGs/providerimages/Westminster-psychiatrist-Andrew-Wenner-Axis-Integrated-Mental-Health.png';
import madisonWalterImage from './SVGs/providerimages/Madison-Walter.png';
import jamieTeunisImage from './SVGs/providerimages/Jamie-teunis.png';
import rossVanAllenImage from './SVGs/providerimages/Ross-van-allen.png';
import victoriaKauseImage from './SVGs/providerimages/Victoria-Kause.png';
import michelleGodwinImage from './SVGs/providerimages/Michelle-Godwin.png';
import roderickObrienImage from './SVGs/providerimages/dr-roderick-obrien-cropped.jpeg';
import bgImg from './SVGs/image001.png';

type ExistingAppointment = {
  appointmentid: string;
  patientid: string;
  providerid: string;
  departmentid: string;
  appointmenttypeid: string;
  scheduleddatetime: string;
  date: string;
  starttime: string;
  duration: number;
  appointmenttype: string;
  appointmentstatus: string;
  patientappointmenttypename?: string;
  providerDetails?: {
    providerid: number;
    firstname: string;
    lastname: string;
    displayname: string;
    schedulingname: string;
    specialty: string;
    entitytype: string;
    providertype: string;
    billable: boolean;
    status: string;
  };
  // Legacy fields for compatibility
  athenaAppointmentId?: string;
  athenaPatientId?: string;
  athenaProviderId?: string;
  athenaDepartmentId?: string;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  reason?: string;
  reasonType?: string;
  providerName?: string;
  departmentName?: string;
};

type Slot = {
  appointmentId: string;
  appointmentTypeId: string;
  startTime: string; // e.g. "09/15/2025T08:30" (new format) or "08:30" (legacy format)
  endTime: string;
  duration?: number;
  appointmentName?: string; // e.g. "PHYSICAL EXAM"
};

type ProviderGroup = {
  providerId: string;
  displayName: string;
  schedulingName: string;
  firstName: string;
  lastName: string;
  specialty: string;
  departmentId: string;
  departmentName: string;
  departmentZip: string;
  departmentAddress: string | null;
  departmentCity: string;
  departmentState: string;
  availableSlots: Slot[];
};

interface Props {
  appointment: ExistingAppointment;
  patientId: string;
  onBack: () => void;
  onDone: () => void;
}

const RescheduleExistingStep: React.FC<Props> = ({ appointment, patientId, onBack, onDone }) => {
  const [providers, setProviders] = React.useState<ProviderGroup[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [selectedSlot, setSelectedSlot] = React.useState<Slot | null>(null);
  const [activeDayByProvider, setActiveDayByProvider] = React.useState<Record<string, string>>({});
  const [showRescheduleConfirm, setShowRescheduleConfirm] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  // Function to get provider image based on provider name or ID
  const getProviderImage = (provider: ProviderGroup): string | undefined => {
    const fullName = `${provider.firstName} ${provider.lastName}`.toLowerCase();
    const schedulingName = provider.schedulingName?.toLowerCase() || '';
    
    // Map providers to their images based on name matching
    // Primary matching by full name
    if (fullName.includes('heather') && fullName.includes('markham')) {
      return heatherMarkhamImage;
    }
    if (fullName.includes('kayla') && fullName.includes('sharpe')) {
      return kaylaSharpeImage;
    }
    if (fullName.includes('benjamin') && fullName.includes('egbers')) {
      return benEgbersImage;
    }
    if (fullName.includes('katharine') && fullName.includes('church')) {
      return katharineChurchImage;
    }
    if (fullName.includes('tiffany') && (fullName.includes('walker') || fullName.includes('lawrence'))) {
      return tiffanyWalkerImage;
    }
    if (fullName.includes('elinor') && fullName.includes('obuckley')) {
      return elinorObuckleyImage;
    }
    if (fullName.includes('sarah') && fullName.includes('mclaughlin')) {
      return sarahMclaughlinImage;
    }
    if (fullName.includes('frances') && fullName.includes('rhodes')) {
      return francesRhodesImage;
    }
    if (fullName.includes('andrew') && fullName.includes('wenner')) {
      return andrewWennerImage;
    }
    if (fullName.includes('madison') && fullName.includes('walter')) {
      return madisonWalterImage;
    }
    if (fullName.includes('jamie') && fullName.includes('teunis')) {
      return jamieTeunisImage;
    }
    if (fullName.includes('ross') && fullName.includes('van') && fullName.includes('allen')) {
      return rossVanAllenImage;
    }
    if (fullName.includes('victoria') && fullName.includes('kause')) {
      return victoriaKauseImage;
    }
    if (fullName.includes('michelle') && fullName.includes('godwin')) {
      return michelleGodwinImage;
    }
    if (fullName.includes('roderick') && fullName.includes('obrien')) {
      return roderickObrienImage;
    }
    
    // Fallback matching by scheduling name
    if (schedulingName.includes('heather_markham')) {
      return heatherMarkhamImage;
    }
    if (schedulingName.includes('kayla_sharpe')) {
      return kaylaSharpeImage;
    }
    if (schedulingName.includes('benjamin_egbers') || schedulingName.includes('dr_benjamin_egbers')) {
      return benEgbersImage;
    }
    if (schedulingName.includes('katharine_church')) {
      return katharineChurchImage;
    }
    if (schedulingName.includes('tiffany_walker') || schedulingName.includes('tiffany_lawrence')) {
      return tiffanyWalkerImage;
    }
    if (schedulingName.includes('elinor_obuckley')) {
      return elinorObuckleyImage;
    }
    if (schedulingName.includes('sarah_mclaughlin')) {
      return sarahMclaughlinImage;
    }
    if (schedulingName.includes('frances_rhodes')) {
      return francesRhodesImage;
    }
    if (schedulingName.includes('andrew_wenner')) {
      return andrewWennerImage;
    }
    if (schedulingName.includes('madison_walter')) {
      return madisonWalterImage;
    }
    if (schedulingName.includes('jamie_teunis')) {
      return jamieTeunisImage;
    }
    if (schedulingName.includes('ross_van_allen')) {
      return rossVanAllenImage;
    }
    if (schedulingName.includes('victoria_kause')) {
      return victoriaKauseImage;
    }
    if (schedulingName.includes('michelle_godwin')) {
      return michelleGodwinImage;
    }
    if (schedulingName.includes('roderick_obrien')) {
      return roderickObrienImage;
    }
    
    // Default fallback image (Heather's image as generic placeholder)
    return heatherMarkhamImage;
  };
  
  // Header/Nav helpers (same as other screens)
  const NAV_PHONE = '(720) 400-7025';
  const NAV_LINKS = [
    { label: 'BOOK ONLINE', href: '#', action: 'refresh' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'BOOK ONLINE', href: '#', action: 'refresh' },
  ];
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 700);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'refresh') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  // Helper functions (defined before they're used)
  const formatTime = (timeString: string) => {
    try {
      if (timeString.includes('T')) {
        // New API format: "09/15/2025T08:30" -> "8:30 AM"
        const [, timePart] = timeString.split('T');
        const [hour, minute] = timePart.split(':');
        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);
        const date = new Date();
        date.setHours(hourNum, minuteNum, 0, 0);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
      } else {
        // Legacy format: "08:30" -> "8:30 AM"
        const [hour, minute] = timeString.split(':');
        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);
        const date = new Date();
        date.setHours(hourNum, minuteNum, 0, 0);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
      }
    } catch (_e) {
      return timeString;
    }
  };


  // Group slots by day (similar to ProviderSelectionStep)
  const groupSlotsByDay = (slots: Slot[]) => {
    const groups: Record<string, Slot[]> = {};
    
    slots.forEach(slot => {
      try {
        // Parse the date from startTime format: "09/15/2025T08:30"
        let date: Date;
        
        if (slot.startTime.includes('T')) {
          // New API format: "09/15/2025T08:30"
          const [datePart, timePart] = slot.startTime.split('T');
          const [month, day, year] = datePart.split('/');
          const [hour, minute] = timePart.split(':');
          
          date = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1, // Month is 0-indexed
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10)
          );
        } else {
          // Fallback for other formats
          date = new Date(slot.startTime);
        }
        
        // Create the day label
        const label = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        
        groups[label] = groups[label] || [];
        groups[label].push(slot);
      } catch (error) {
        // Fallback to current date if parsing fails
        // Fallback: use today's date
        const today = new Date();
        const label = today.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        groups[label] = groups[label] || [];
        groups[label].push(slot);
      }
    });
    
    return groups;
  };

  React.useEffect(() => {
    const fetchSlots = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use direct pattern with departmentId and appointmentTypeId (no zipCode/radiusMiles required)
        const data = await getProviderSpecificOpenSlots(String(appointment.providerid || appointment.athenaProviderId), {
          departmentId: String(appointment.departmentid || appointment.athenaDepartmentId),
          appointmentTypeId: String(appointment.appointmenttypeid || appointment.appointmentTypeId),
          patientId,
          // Don't pass startDate, endDate, limit, offset - let backend auto-calculate
        });
        
        console.log('🔍 RescheduleExistingStep - API Response:', data);
        
        const list: ProviderGroup[] = Array.isArray(data) ? data : (data?.providers || []);
        console.log('🔍 RescheduleExistingStep - Providers to display:', list);
        
        // Show all returned providers - backend already filters by department and appointment type
        setProviders(list);
      } catch (e: any) {
        const userFriendlyError = getUserFriendlyErrorMessage(e, 'loading appointment times');
        setError(userFriendlyError);
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [appointment, patientId]);

  const handleConfirmClick = () => {
    if (!selectedSlot) return;
    setShowRescheduleConfirm(true);
  };

  const handleConfirmReschedule = async () => {
    if (!selectedSlot) return;
    
    try {
      setLoading(true);
      setError(null);
      await rescheduleAppointment({
        appointmentId: appointment.appointmentid || appointment.athenaAppointmentId || '',
        targetAppointmentId: selectedSlot.appointmentId,
        departmentId: appointment.departmentid || appointment.athenaDepartmentId || '',
        providerId: appointment.providerid || appointment.athenaProviderId || '',
        patientId,
        bookingNote: 'Patient requested reschedule',
      });
      setShowRescheduleConfirm(false);
      setSuccess(true);
    } catch (e: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(e, 'rescheduling appointment');
      setError(userFriendlyError);
      setShowRescheduleConfirm(false); // Close the reschedule confirmation modal first
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.landingRoot}>
        <div className={styles.topBanner}>
          <img src={bgImg} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          <div className={styles.navBar}>
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Finding New Times</h1>
            <div className={styles.bannerSubtitle}>We're looking for openings with your current provider.</div>
          </div>
        </div>
        <div className={styles.landingMain}>
          <div className={styles.loadingSpinner}><div className={styles.spinner}></div></div>
        </div>
      </div>
    );
  }

  // Error modal will be shown via showErrorModal state, no need for separate error page

  if (success) {
    return (
      <div className={styles.landingRoot}>
        <div className={styles.topBanner}>
          <img src={bgImg} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          <div className={styles.navBar}>
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Appointment Rescheduled!</h1>
            <div className={styles.bannerSubtitle}>
              Your appointment has been successfully rescheduled
            </div>
          </div>
        </div>

        <div className={styles.landingMain}>
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#10b981" strokeWidth="2"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <h2 className={styles.successTitle}>Reschedule Confirmed!</h2>
            <p className={styles.successSubtitle}>
              Your appointment has been successfully rescheduled. You will receive a confirmation email shortly with all the details.
            </p>
            
            <div className={styles.confirmationDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>What's Next:</span>
                <span className={styles.detailValue}>
                  Check your email for updated appointment details and any pre-appointment instructions.
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reminder:</span>
                <span className={styles.detailValue}>
                  You'll receive a reminder 24 hours before your appointment.
                </span>
              </div>
            </div>

            <div className={styles.actionButtons}>
              <button
                className={styles.primaryButton}
                onClick={onDone}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>

        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
            <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={styles.landingRoot}>
      {/* Top Banner with Background Image and Overlay */}
      <div className={styles.topBanner}>
        <img src={bgImg} alt="Banner" className={styles.bannerImg} />
        <div className={styles.bannerOverlay} />
        {/* Nav Bar */}
        <div className={styles.navBar}>
          <span className={styles.navPhone}>{NAV_PHONE}</span>
          <div className={styles.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={styles.navLink} onClick={() => {}}>{link.label}</button>
            ))}
          </div>
        </div>
        {/* Banner Content */}
        <div className={styles.bannerContent}>
          <h1 className={styles.bannerTitle}>Select a New Time</h1>
          <div className={styles.bannerSubtitle}>
            Choose a new appointment time with your provider
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.landingMain}>
        <div className={styles.landingHeader}>
          <h2 className={styles.landingTitle}>Choose your Provider</h2>
          <div className={styles.landingSubtitle}>Select a new time for your appointment</div>
        </div>

        <div className={styles.providersContainer}>
          {providers.map((provider) => {
            const grouped = groupSlotsByDay(provider.availableSlots || []);
            const dayLabels = Object.keys(grouped);
            const providerKey = provider.providerId;
            const activeDay = activeDayByProvider[providerKey] || dayLabels[0] || '';

            return (
              <div
                key={provider.providerId}
                className={styles.providerCard}
              >
                <div className={styles.providerHeader}>
                  <div className={styles.providerIcon}>
                    {getProviderImage(provider) ? (
                      <img 
                        src={getProviderImage(provider)} 
                        alt={`${provider.firstName} ${provider.lastName}`}
                        className={styles.providerImage}
                      />
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    )}
                  </div>
                  <div className={styles.providerInfo}>
                    <h3 className={styles.providerName}>
                      {provider.displayName || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || 'Provider'}
                    </h3>
                    <div className={styles.providerLocation}>
                      {provider.departmentName || 'Department'}
                    </div>
                  </div>
                </div>

                <div className={styles.slotsContainer}>
                  <h4 className={styles.slotsTitle}>Available Appointments</h4>

                  {/* Day Tabs */}
                  {dayLabels.length > 0 && (
                    <div className={styles.dayTabs}>
                      {dayLabels.map((label) => (
                        <button
                          key={label}
                          className={`${styles.dayTab} ${label === activeDay ? styles.dayTabActive : ''}`}
                          onClick={() => setActiveDayByProvider(prev => ({ ...prev, [providerKey]: label }))}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className={styles.slotsGrid}>
                    {(grouped[activeDay] || []).map((slot) => (
                      <button
                        key={slot.appointmentId}
                        className={`${styles.slotButton} ${selectedSlot?.appointmentId === slot.appointmentId ? styles.selectedSlot : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        {formatTime(slot.startTime)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.actions}>
          <button onClick={onBack} className={styles.backButton}>← Back</button>
          <button
            onClick={handleConfirmClick}
            className={`${styles.primaryButton} ${!selectedSlot ? styles.disabled : ''}`}
            disabled={!selectedSlot}
          >
            Confirm Reschedule
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.landingFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
          <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
          <div className={styles.footerRating}>
            <span className={styles.footerStars}>★★★★★</span>
            <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRescheduleConfirm}
        onClose={() => setShowRescheduleConfirm(false)}
        onConfirm={handleConfirmReschedule}
        title="Confirm Reschedule"
        message="Are you sure you want to reschedule to the selected time?"
        confirmText="Yes, Reschedule"
        cancelText="Cancel"
        confirmButtonStyle="primary"
        isLoading={loading}
      />

      {/* Error Modal */}
      {showErrorModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Unable to Load Times</h3>
            <p className={styles.modalText}>{error}</p>
            <div className={styles.modalButtons}>
              <button
                className={styles.modalButtonSecondary}
                onClick={() => {
                  setShowErrorModal(false);
                  setShowRescheduleConfirm(false); // Ensure reschedule modal is closed
                  setError(null);
                  onBack();
                }}
              >
                Go Back
              </button>
              <button
                className={styles.modalButtonPrimary}
                onClick={() => {
                  setShowErrorModal(false);
                  setShowRescheduleConfirm(false); // Ensure reschedule modal is closed
                  setError(null);
                  // Retry loading
                  window.location.reload();
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RescheduleExistingStep;
